import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getListeTacheActive } from "../../../data/params/tache";
import moment from "moment";
import { getListeInterventionActive } from "../../../data/params/intervention";
import { getListeUserMission } from "../../../data/exercice";
import {
  Button,
  Col,
  Divider,
  Input,
  Menu,
  message as msg,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toAbsoluteUrl } from "../../../helpers/AssetsHelpers";
import clsx from "clsx";
import { ClockCircleOutlined } from "@ant-design/icons";
import FormError from "../../common/Form/FormError";
import LoadingComponent from "../../modals/LoadingComponent";
import {
  faEye,
  faIdBadge,
  faUsersViewfinder,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Identification } from "./Identification";
import { getListeIdentificationSommaire } from "../../../data/rateau";
import PageLabel from "../../common/PageLabel/PageLabel";
import IdImage from "../../../assets/images/user-identification.PNG";
import { appTheme } from "../../../config/theme";

const { Title } = Typography;

/**
 * Configuration des onglets du menu
 */
const MENU_ITEMS = [
  {
    label: "Identification",
    icon: <FontAwesomeIcon icon={faIdBadge} />,
    key: "0",
  },
  {
    label: "Liste des identifications",
    icon: <FontAwesomeIcon icon={faUsersViewfinder} />,
    key: "1",
  },
];

/**
 * Schéma de validation Yup pour le formulaire d'identification
 */
const validationSchema = Yup.object().shape({
  tache_id: Yup.number()
    .required("Veuillez sélectionner la tâche")
    .typeError("La tâche est obligatoire"),
  intervention_id: Yup.number()
    .required("Veuillez sélectionner l'intervention")
    .typeError("L'intervention est obligatoire"),
});

/**
 * Composant principal pour l'identification des utilisateurs dans un cycle
 */
const UserCycleIdentification = ({
  setDate,
  dateDebut,
  setHeureEntree,
  mission,
  codeSommaire,
  loading,
  setIsAuthorized,
  isAuthorized,
  setTache_id,
  setIntervention_id,
  setUser_id,
  setActiveStep,
}) => {
  // États locaux
  const [currentTab, setCurrentTab] = useState("0");
  const [taches, setTaches] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // ✅ Changé de true à false
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [identifications, setIdentifications] = useState([]);

  // Récupération de l'utilisateur depuis Redux
  const user = useSelector((state) => state.user);

  /**
   * Initialise la date et l'heure d'entrée au montage du composant
   */
  useEffect(() => {
    const currentDate = new Date();
    setDate(currentDate);
    setHeureEntree(moment(currentDate).format("HH:mm:ss"));
  }, [setDate, setHeureEntree]);

  /**
   * Définit l'ID utilisateur quand l'utilisateur est disponible
   */
  useEffect(() => {
    if (user?.id) {
      setUser_id(user.id);
    }
  }, [setUser_id, user]);

  /**
   * Charge la liste des identifications pour l'onglet "Liste des identifications"
   */
  useEffect(() => {
    if (codeSommaire && currentTab === "1") {
      fetchIdentifications();
    }
  }, [mission.id, codeSommaire, currentTab]);

  /**
   * Récupère le rôle de l'utilisateur pour cette mission
   */
  useEffect(() => {
    if (user?.id && mission?.id) {
      fetchUserRole();
    }
  }, [user, mission]);

  /**
   * Charge les tâches et interventions autorisées selon le rôle
   */
  useEffect(() => {
    if (userRole) {
      fetchUserTasks();
      fetchUserInterventions();
    }
  }, [userRole]);

  /**
   * Récupère les identifications existantes
   */
  const fetchIdentifications = async () => {
    try {
      const response = await getListeIdentificationSommaire(
        mission.id,
        codeSommaire
      );

      setIdentifications(
        response.result.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        ) || []
      );
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des identifications:",
        error
      );
    }
  };

  /**
   * Récupère le rôle de l'utilisateur pour cette mission
   */
  const fetchUserRole = async () => {
    try {
      const response = await getListeUserMission(user.id);
      if (response) {
        const userMission = response.find(
          (item) => item.exercice_id === mission.id
        );
        setUserRole(userMission?.Lea_RoleMission || null);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du rôle:", error);
    } finally {
    }
  };

  /**
   * Récupère les tâches autorisées pour l'utilisateur
   */
  const fetchUserTasks = async () => {
    try {
      const response = await getListeTacheActive();
      if (response?.result?.length > 0) {
        const authorizedTasks = response.result.filter((task) =>
          userRole?.actions?.includes(task.id)
        );
        setTaches(authorizedTasks);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des tâches:", error);
    }
  };

  /**
   * Récupère les interventions autorisées pour l'utilisateur
   */
  const fetchUserInterventions = async () => {
    try {
      const response = await getListeInterventionActive();
      if (response?.result?.length > 0) {
        const authorizedInterventions = response.result.filter((intervention) =>
          userRole?.intervention?.includes(intervention.id)
        );
        setInterventions(authorizedInterventions);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des interventions:", error);
    }
  };

  /**
   * Gère la validation du formulaire d'identification
   */
  const handleValidation = async (values) => {
    setIsAuthenticating(true);

    // Simulation d'une authentification (remplacer par votre logique)
    setTimeout(() => {
      setIsAuthorized(true);
      setTache_id(values.tache_id);
      setIntervention_id(values.intervention_id);
      setActiveStep((prevStep) => prevStep + 1);

      msg.success(
        `Bienvenue ${user?.codeCivilite || ""} ${user?.nom_user || ""} ${
          user?.prenom_user || ""
        }`
      );

      setIsAuthenticating(false);
    }, 1500);
  };

  /**
   * Gère le changement d'onglet
   */
  const handleTabChange = useCallback((e) => {
    setCurrentTab(e.key);
  }, []);

  /**
   * Composant de formulaire d'identification
   * ✅ Utilisation de useCallback avec dépendances pour forcer le re-render
   */
  const IdentificationForm = useCallback(
    () => (
      <Row gutter={[16, 16]}>
        {/* Image d'illustration */}
        <Col
          xs={0}
          sm={0}
          md={0}
          lg={10}
          xl={10}
          xxl={10}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img src={IdImage} alt="Identification" className="w-75" />
        </Col>

        {/* Formulaire */}
        <Col xs={24} sm={24} md={24} lg={14} xl={14} xxl={14}>
          <Formik
            initialValues={{
              tache_id: "",
              intervention_id: "",
            }}
            onSubmit={handleValidation}
            validationSchema={validationSchema}
          >
            {({ setFieldValue, touched, errors, handleSubmit }) => (
              <Form>
                <Divider />
                <Row gutter={[16, 16]}>
                  {/* Informations utilisateur */}
                  <Col span={16}>
                    <div className="symbol symbol-circle symbol-100px overflow-hidden me-3">
                      {user?.avatar ? (
                        <div className="symbol-label">
                          <img
                            src={toAbsoluteUrl(`/media/${user.avatar}`)}
                            alt={`${user.nom_user} ${user.prenom_user}`}
                            className="w-100"
                          />
                        </div>
                      ) : (
                        <div
                          className={clsx(
                            "symbol-label fs-3",
                            "bg-light-green"
                          )}
                        >
                          <img
                            src={toAbsoluteUrl(
                              user.codeCivilite === "M."
                                ? "/media/avatars/male.jpg"
                                : "/media/avatars/female.jpg"
                            )}
                            alt={`${user.nom_user} ${user.prenom_user}`}
                            className="w-100"
                          />
                        </div>
                      )}
                    </div>
                    <div className="d-flex flex-column">
                      <span
                        className="fs-3"
                        style={{ color: "#24247a", fontWeight: "800" }}
                      >
                        {`${user.nom_user || ""} ${user.prenom_user || ""}`}
                      </span>
                      <span className="fw-bolder fs-5 text-black">
                        {userRole?.libelle}
                      </span>
                    </div>
                  </Col>

                  {/* Horodatage */}
                  <Col span={8}>
                    <span
                      className="chart-blue display-block fs-6"
                      style={{ fontWeight: 700 }}
                    >
                      <ClockCircleOutlined style={{ fontSize: "18px" }} />{" "}
                      {moment().format("DD/MM/YYYY à HH:mm")}
                    </span>
                  </Col>

                  {/* Sélection de la tâche */}
                  <Col span={24}>
                    <Space.Compact block>
                      <Input
                        style={{ width: "30%" }}
                        value="Tâches"
                        readOnly={true}
                      />
                      <Select
                        showSearch
                        placeholder="Sélectionnez la tâche..."
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          option.label
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        options={taches.map((task) => ({
                          value: task.id,
                          label: task.libelle,
                        }))}
                        onChange={(value) => setFieldValue("tache_id", value)}
                        size="large"
                        style={{ width: "70%" }}
                      />
                    </Space.Compact>
                    {touched.tache_id && errors.tache_id && (
                      <FormError fieldName="tache_id" />
                    )}
                  </Col>

                  {/* Sélection de l'intervention */}
                  <Col span={24}>
                    <Space.Compact block>
                      <Input
                        style={{ width: "30%" }}
                        value="Intervention"
                        readOnly={true}
                      />
                      <Select
                        showSearch
                        placeholder="Sélectionnez l'intervention..."
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          option.label
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        options={interventions.map((intervention) => ({
                          value: intervention.id,
                          label: intervention.libelle,
                        }))}
                        onChange={(value) =>
                          setFieldValue("intervention_id", value)
                        }
                        size="large"
                        style={{ width: "70%" }}
                      />
                    </Space.Compact>
                    {touched.intervention_id && errors.intervention_id && (
                      <FormError fieldName="intervention_id" />
                    )}
                  </Col>

                  {/* Bouton de validation */}
                  <Col span={24}>
                    <Button
                      type="primary"
                      block
                      size="large"
                      disabled={loading || isAuthenticating}
                      onClick={handleSubmit}
                    >
                      {!(loading || isAuthenticating) ? (
                        "Enregistrer"
                      ) : (
                        <span
                          style={{
                            color: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <div
                            className="spinner-border spinner-border-sm me-2"
                            style={{ width: "16px", height: "16px" }}
                          ></div>
                          Veuillez patienter...
                        </span>
                      )}
                    </Button>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    ),
    [
      taches,
      interventions,
      user,
      userRole,
      loading,
      isAuthenticating,
      handleValidation,
    ]
  ); // ✅ Dépendances

  /**
   * Composant d'accueil après authentification
   */
  const WelcomeMessage = () => (
    <div className="d-flex flex-column align-center align-center align-items-center mt-10">
      <Title
        style={{
          fontWeight: "600",
          color: appTheme.colors.success,
          textAlign: "center",
        }}
        level={3}
      >
        <span className="text-black me-2">
          Bienvenue {user?.codeCivilite || ""}
        </span>
        {user?.nom_user || ""} {user?.prenom_user || ""}
      </Title>
      <div className="d-flex flex-column align-items-center mt-3">
        <span>Rôle: {userRole?.libelle}</span>
        <span>
          Date d'identification: {moment(dateDebut).format("DD/MM/YYYY HH:mm")}
        </span>
        <span>
          Temps passé dans le cycle:{" "}
          {moment().diff(moment(dateDebut), "minutes")} minutes
        </span>
      </div>
    </div>
  );

  /**
   * Rendu conditionnel selon l'onglet sélectionné
   * ✅ Mise à jour des dépendances
   */
  const renderCurrentTab = useCallback(() => {
    switch (currentTab) {
      case "0":
        return isAuthorized ? <WelcomeMessage /> : <IdentificationForm />;
      case "1":
        return (
          <Identification identifications={identifications} mission={mission} />
        );
      default:
        return null;
    }
  }, [
    currentTab,
    isAuthorized,
    identifications,
    mission,
    userRole,
    dateDebut,
    IdentificationForm,
  ]); // ✅ Ajouter IdentificationForm

  // ✅ Condition d'affichage du loader plus spécifique
  if (!isAuthorized && isLoading) {
    return (
      <LoadingComponent
        loading={isLoading}
        setLoading={setIsLoading}
        message="Veuillez patienter, nous chargeons les ressources nécessaires au traitement de ce cycle."
      />
    );
  }

  return (
    <div className="px-5 py-5">
      <PageLabel label="Identification des tâches et interventions des collaborateurs" />

      {/* Menu de navigation */}
      <Menu
        onClick={handleTabChange}
        selectedKeys={[currentTab]}
        mode="horizontal"
        items={MENU_ITEMS}
        style={{ marginBottom: "5px", fontWeight: "bold" }}
      />

      {/* Contenu de l'onglet actuel */}
      {renderCurrentTab()}
    </div>
  );
};

export default UserCycleIdentification;
